import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { useUpdateProfile } from '@/api/hooks/useAccount';
import type { AppError } from '@/api/client';

/**
 * Personal-info card on the customer account page (Sprint 7
 * Evening 10). Saves through {@code PUT /users/me} (firstName /
 * lastName / locale). Email is rendered read-only — changing email
 * requires re-verification, deferred to Phase 2.
 *
 * <p>Locale change triggers an immediate i18n switch on the
 * frontend so the UI updates without a reload. The server-side
 * locale lives on the user row and drives email/SMS template
 * selection in {@code NotificationService}.
 */
export default function PersonalInfoSection() {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const updateProfile = useUpdateProfile();

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  // Initialise from i18n.language (the current page locale), not from
  // user.locale (the stale profile value). After PR IV's single-source-
  // of-truth design the two converge on user resolve, but the dropdown
  // also needs to reflect a header-toggle change made before the user
  // visited this page. See doc/DECISIONS.md "Language Switcher Single
  // Source of Truth".
  const [locale, setLocale] = useState(i18n.language);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
      // Mirror the active page locale (which AuthProvider keeps in
      // sync with user.locale on resolve).
      setLocale(i18n.language);
    }
  }, [user, i18n.language]);

  const dirty =
    firstName !== (user?.firstName ?? '') ||
    lastName !== (user?.lastName ?? '') ||
    locale !== i18n.language;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate(
      { firstName, lastName, locale },
      {
        onSuccess: async () => {
          await refreshUser();
          // Switch UI language immediately — the user expects the
          // locale change to take effect on the next click, not after
          // a manual reload.
          if (locale === 'en' || locale === 'fr') {
            await i18n.changeLanguage(locale);
          }
          toast.success(t('account.profile_updated'));
        },
        onError: (err) => {
          const e = err as unknown as AppError;
          toast.error(e?.message ?? t('common.error'));
        },
      },
    );
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t('account.personal_info')}
      </h2>
      <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t('account.first_name')}>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputCls}
            maxLength={100}
            autoComplete="given-name"
          />
        </Field>
        <Field label={t('account.last_name')}>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={inputCls}
            maxLength={100}
            autoComplete="family-name"
          />
        </Field>
        <Field label={t('account.email')}>
          <input
            type="email"
            value={user?.email ?? ''}
            disabled
            className={inputCls + ' opacity-60 cursor-not-allowed'}
          />
          <p className="mt-1 text-xs text-neutral-500">{t('account.email_readonly')}</p>
        </Field>
        <Field label={t('account.language')}>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className={inputCls}
          >
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
        </Field>
        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={!dirty || updateProfile.isPending}
            className="rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateProfile.isPending ? t('common.saving') : t('account.save_changes')}
          </button>
        </div>
      </form>
    </section>
  );
}

const inputCls =
  'w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-medium text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
